import { gql } from '@apollo/client';
import { t } from '@lingui/macro';

import { TL2Category$L4Group } from 'Components/CategoryGroups/CategoryGroups.helpers';
import { TFeedback } from 'Components/FeedbackCard/FeedbackCard.helpers';
import { TLatestRequest } from 'Components/LatestRequests/LatestRequests.helpers';
import { TProvidersSlider$Provider } from 'Components/ProvidersSlider/ProvidersSlider';
import { cityNameShortener } from 'common/helpers/helpers';

/**
 * SEO: GS-7366 Create new titles pattern for L2 + city pages
 * see https://naspersclassifieds.atlassian.net/browse/GS-7366
 */

export const getSeoTitleByLength = (name: string, cityName: string, providersCount: number): string => {
  let iteration = 0;
  const MAX_TITLE_LENGTH = 50;

  const getTitleByLength = (): string => {
    const cityNameShort: string | undefined = cityNameShortener(cityName);
    const countSegment = t`${providersCount} wykonawców`;
    const priceOnlySegment = t`Cena`;
    const reviewOnlySegment = t`Opinie`;
    const priceAndReviewSegment = [priceOnlySegment, reviewOnlySegment].join(', ');

    const titlesMap: any = {
      0: `${name} ${cityNameShort}`,
      1: `${name} ${cityNameShort} - ${priceOnlySegment}`,
      2: `${name} ${cityNameShort} - ${reviewOnlySegment}`,
      3: `${name} ${cityNameShort} - ${priceAndReviewSegment}`,
      4: `${name} ${cityNameShort} - ${countSegment}`,
      5: `${name} ${cityNameShort} - ${countSegment} - ${priceOnlySegment}`,
      6: `${name} ${cityNameShort} - ${countSegment} - ${reviewOnlySegment}`,
      7: `${name} ${cityNameShort} - ${countSegment} - ${priceAndReviewSegment}`,
    };
    const titlesLength: number = Object.keys(titlesMap).length - 1;
    const currentTitleLength = titlesMap[iteration].length;
    if (currentTitleLength < MAX_TITLE_LENGTH && iteration !== titlesLength) {
      iteration++;
      getTitleByLength();
    }

    return titlesMap[iteration === 0 || iteration === titlesLength ? iteration : iteration - 1];
  };
  return getTitleByLength();
};

export type L2CategoryResponse = {
  l2BySlug: TL2Category;
};

export type L2CategoryCityResponse = {
  cityBySlug: TCity;
};

export type TL2Category = {
  id: string;
  slug: string;
  name: string;
  ogDescription: string;
  seoDescription?: string;
  questionsAndAnswers: string;
  providerCount: number;
  requestCount: number;
  feedbacksInfo: {
    averageValue: number;
    count: number;
  };
  l4Groups: TL2Category$L4Group[];
  requests: TLatestRequest[];
  feedbacks: TFeedback[];
  providers: TProvidersSlider$Provider[];
  isPayWithFixly: boolean;
};

export type TCity = {
  id: string;
  slug: string;
  name: string;
  nameLocativeCase: string;
  lat: string;
  long: string;
  region: {
    name: string;
  };
};

export const l2Redirect = {
  'budowa-i-remonty': 'remont',
  'uslugi-remontowe': 'remont',
  'uslugi-domowe': '/',
  'uslugi-transportowe': '/',
  'uslugi-motoryzacyjne': '/',
  'budowa-i-remont': 'remont',
  'meble': 'meble-i-zabudowa',
  'uslugi-specjalistyczne': '/',
  'dom-i-biuro': 'remont',
  'nie-kupuj': 'montaz-i-naprawa',
};

export const L2CategoryQuery = gql`
  query L2category($slug: String!, $showHiddenCategories: Boolean, $city: String) {
    l2BySlug(slug: $slug, showHiddenCategories: $showHiddenCategories, city: $city) {
      id
      slug
      name
      providerCount
      requestCount
      ogDescription
      seoDescription
      questionsAndAnswers
      feedbacksInfo {
        averageValue
        count
      }
      l4Groups {
        name
        l2Id
        l4Categories {
          id
          name
          slug
          mobileCoverUrl
          l3 {
            id
            slug
            name
          }
        }
      }
      feedbacks {
        id
        value
        comment
        createdAt
        user {
          id
          name
          avatar
          city
          gender
        }
        provider {
          id
          name
          slug
          avatar
          city
        }
      }
      requests {
        id
        user {
          id
          name
          gender
          avatar
        }
        providers {
          id
          avatar
        }
        details {
          question_id
          question
          answer_id
          answer
        }
      }
      providers {
        id
        slug
        firstName
        lastName
        publicName
        avatarUrl
        companyName
        rating
        feedbacksCount
        shortDescription
        cityName
        dateRegistered
        featured
        yearsOfExperience
        yearEstablished
      }
      isPayWithFixly
    }
  }
`;

export const L2CategoryCityQuery = gql`
  query L2categoryCity($slug: String!) {
    cityBySlug(slug: $slug) {
      id
      slug
      name
      nameLocativeCase
      lat
      long
      region {
        id
        name
      }
    }
  }
`;

export const remoteJobCategory = (id: string) => +id === 123;

const MAX_FEATURED_CATEGORIES_COUNT = 4;

export const extractFeaturedL4 = (groups: TL2Category$L4Group[]) => {
  const featuredCategoriesGroup = groups.find((categoryGroup) => categoryGroup.name === 'featured');

  if (featuredCategoriesGroup) {
    return featuredCategoriesGroup.l4Categories.slice(0, MAX_FEATURED_CATEGORIES_COUNT);
  }

  return [];
};
